import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Save, Download, Music, Clock, Zap, Key,
  GripVertical, Trash2, Plus, Edit3, AlertTriangle,
  CheckCircle2, XCircle, ChevronDown, ChevronUp,
  Loader2, Search, X, ExternalLink, Play, Pause,
  Sparkles, Waves, Disc, Share2, Award, RefreshCw,
  Users, Headphones, Lightbulb, MessageCircle, Lock, Unlock, Info
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import ExportModal from './ExportModal';
import CamelotWheel from './CamelotWheel';
import ShareModal from './ShareModal';
import SwapTrackModal from './SwapTrackModal';
import SetPreviewPlayer from './SetPreviewPlayer';
import MixPointAdvisor from './MixPointAdvisor';
import WaveformPreview from './WaveformPreview';
import CollabDiscussionPanel from './CollabDiscussionPanel';
import './SetBuilder.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Camelot wheel compatibility
const CAMELOT_WHEEL = {
  // Minor keys (A)
  '1A': { compatible: ['1A', '12A', '2A', '1B'], energy: 'up' },
  '2A': { compatible: ['2A', '1A', '3A', '2B'], energy: 'up' },
  '3A': { compatible: ['3A', '2A', '4A', '3B'], energy: 'up' },
  '4A': { compatible: ['4A', '3A', '5A', '4B'], energy: 'up' },
  '5A': { compatible: ['5A', '4A', '6A', '5B'], energy: 'up' },
  '6A': { compatible: ['6A', '5A', '7A', '6B'], energy: 'up' },
  '7A': { compatible: ['7A', '6A', '8A', '7B'], energy: 'up' },
  '8A': { compatible: ['8A', '7A', '9A', '8B'], energy: 'up' },
  '9A': { compatible: ['9A', '8A', '10A', '9B'], energy: 'up' },
  '10A': { compatible: ['10A', '9A', '11A', '10B'], energy: 'up' },
  '11A': { compatible: ['11A', '10A', '12A', '11B'], energy: 'up' },
  '12A': { compatible: ['12A', '11A', '1A', '12B'], energy: 'up' },
  // Major keys (B)
  '1B': { compatible: ['1B', '12B', '2B', '1A'], energy: 'up' },
  '2B': { compatible: ['2B', '1B', '3B', '2A'], energy: 'up' },
  '3B': { compatible: ['3B', '2B', '4B', '3A'], energy: 'up' },
  '4B': { compatible: ['4B', '3B', '5B', '4A'], energy: 'up' },
  '5B': { compatible: ['5B', '4B', '6B', '5A'], energy: 'up' },
  '6B': { compatible: ['6B', '5B', '7B', '6A'], energy: 'up' },
  '7B': { compatible: ['7B', '6B', '8B', '7A'], energy: 'up' },
  '8B': { compatible: ['8B', '7B', '9B', '8A'], energy: 'up' },
  '9B': { compatible: ['9B', '8B', '10B', '9A'], energy: 'up' },
  '10B': { compatible: ['10B', '9B', '11B', '10A'], energy: 'up' },
  '11B': { compatible: ['11B', '10B', '12B', '11A'], energy: 'up' },
  '12B': { compatible: ['12B', '11B', '1B', '12A'], energy: 'up' },
};

const getTransitionQuality = (fromKey, toKey, fromBpm, toBpm) => {
  if (!fromKey || !toKey) return 'unknown';
  
  const fromData = CAMELOT_WHEEL[fromKey];
  if (!fromData) return 'unknown';
  
  const isHarmonic = fromData.compatible.includes(toKey);
  const bpmDiff = Math.abs((fromBpm || 0) - (toBpm || 0));
  
  if (isHarmonic && bpmDiff <= 2) return 'perfect';
  if (isHarmonic && bpmDiff <= 5) return 'good';
  if (isHarmonic || bpmDiff <= 3) return 'acceptable';
  return 'caution';
};

const SetBuilder = ({ 
  crate: initialCrate, 
  onClose, 
  onUpdate,
  onAddFromDiscovery,
  onAddFromSpotify,
  onAddFromSoundCloud,
  onAddFromFavorites,
  onAddFromPlaylists,
  onAddFromLocal,
  spotifyConnected,
  soundcloudConnected
}) => {
  const [crate, setCrate] = useState(initialCrate);
  const [tracks, setTracks] = useState(initialCrate?.tracks || []);
  const [trackListKey, setTrackListKey] = useState(0); // Force re-render key
  const [isLoading, setIsLoading] = useState(false);
  
  // Debug: Log whenever tracks state changes
  console.log('SetBuilder render - tracks count:', tracks.length, 'first track:', tracks[0]?.title, 'trackListKey:', trackListKey);
  const [isSaving, setIsSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [showAddTrack, setShowAddTrack] = useState(false);
  const [editingTrack, setEditingTrack] = useState(null);
  const [draggedTrack, setDraggedTrack] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isApplyingAI, setIsApplyingAI] = useState(null); // 'harmonic' or 'energy'
  const [mbEnriching, setMbEnriching] = useState(false);
  const [aiOrderApplied, setAiOrderApplied] = useState(null); // Track which AI ordering is active
  const [originalTrackOrder, setOriginalTrackOrder] = useState(null); // Store original order for toggle off
  const [showExportModal, setShowExportModal] = useState(false);
  const [showCamelotWheel, setShowCamelotWheel] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [swappingTrack, setSwappingTrack] = useState(null); // track position to swap
  
  // New Phase 7 features
  const [showSetPreview, setShowSetPreview] = useState(false);
  const [expandedAdvisor, setExpandedAdvisor] = useState(null); // Index of expanded mix advisor
  const [showMixAdvisors, setShowMixAdvisors] = useState(false); // Toggle all mix advisors - default OFF
  
  // Collaboration features
  const [showCollabPanel, setShowCollabPanel] = useState(false);
  const [collabMode, setCollabMode] = useState(initialCrate?.collab_mode || 'private');
  const [lockedTracks, setLockedTracks] = useState(initialCrate?.locked_tracks || []);
  const [isUpdatingCollab, setIsUpdatingCollab] = useState(false);
  
  // BEATSEEK Audio Analysis
  const [analysisType, setAnalysisType] = useState(null); // 'core', 'plus', 'full'
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState({ current: 0, total: 0 });
  const [analysisDetailTrack, setAnalysisDetailTrack] = useState(null); // Track for detail popup

  const getAuthToken = useCallback(() => localStorage.getItem('beatseek_token'), []);

  // Fetch crate stats
  const fetchStats = useCallback(async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/${crate.id}/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [crate.id, getAuthToken]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats, tracks]);

  // Close analysis detail modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && analysisDetailTrack) {
        setAnalysisDetailTrack(null);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [analysisDetailTrack]);

  // Add track to crate
  const addTrack = async (trackData) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/${crate.id}/tracks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(trackData)
      });

      if (!response.ok) throw new Error('Failed to add track');

      const data = await response.json();
      setTracks(prev => [...prev, data.track]);
      setShowAddTrack(false);
      fetchStats();
    } catch (err) {
      console.error('Error adding track:', err);
      setError(err.message);
    }
  };

  // Remove track from crate
  const removeTrack = async (position) => {
    if (!window.confirm('Remove this track from the crate?')) return;
    
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/${crate.id}/tracks/${position}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to remove track');

      setTracks(prev => prev.filter((_, i) => i !== position).map((t, i) => ({ ...t, position: i })));
      fetchStats();
    } catch (err) {
      console.error('Error removing track:', err);
    }
  };

  // Update track
  const updateTrack = async (position, updates) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/${crate.id}/tracks/${position}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) throw new Error('Failed to update track');

      const data = await response.json();
      setTracks(prev => prev.map((t, i) => i === position ? data.track : t));
      setEditingTrack(null);
    } catch (err) {
      console.error('Error updating track:', err);
    }
  };

  // Reorder tracks
  const reorderTracks = async (newOrder) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/${crate.id}/reorder-tracks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          track_positions: newOrder.map((track, index) => ({
            track_id: track.track_id,
            position: index
          }))
        })
      });

      if (!response.ok) throw new Error('Failed to reorder tracks');

      const data = await response.json();
      setTracks(data.tracks);
    } catch (err) {
      console.error('Error reordering tracks:', err);
    }
  };

  // Helper: Check if track order changed
  const didOrderChange = (oldTracks, newTracks) => {
    if (oldTracks.length !== newTracks.length) return true;
    for (let i = 0; i < oldTracks.length; i++) {
      if (oldTracks[i].track_id !== newTracks[i].track_id) return true;
    }
    return false;
  };

  // Apply AI Harmonic Ordering (toggle on/off)
  const applyHarmonicOrdering = async () => {
    if (tracks.length < 2) return;
    
    // If already active, toggle OFF - restore original order
    if (aiOrderApplied === 'harmonic') {
      if (originalTrackOrder) {
        await reorderTracks(originalTrackOrder);
      }
      setAiOrderApplied(null);
      setOriginalTrackOrder(null);
      return;
    }
    
    // Store original order before applying AI (if not already stored)
    const currentOrder = [...tracks];
    if (!originalTrackOrder) {
      setOriginalTrackOrder(currentOrder);
    }
    
    setIsApplyingAI('harmonic');
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/${crate.id}/ai/harmonic-order`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to apply harmonic ordering');

      const data = await response.json();
      console.log('Harmonic order response:', data.success, 'tracks:', data.tracks?.length);
      if (data.success && data.tracks) {
        console.log('Setting tracks from harmonic order:', data.tracks.map(t => t.title).slice(0, 4));
        // Create new array with updated position values to force React re-render
        const reorderedTracks = data.tracks.map((t, i) => ({ ...t, position: i }));
        
        // Check if order actually changed
        const orderChanged = didOrderChange(currentOrder, reorderedTracks);
        
        setTracks(reorderedTracks);
        setTrackListKey(prev => prev + 1); // Force list re-render
        setAiOrderApplied('harmonic');
        
        // Alert user based on result
        if (!orderChanged) {
          alert('✨ Already Harmonically Optimized!\n\nYour tracks are already in a great harmonic order. The keys flow smoothly together, so no changes were needed.');
        }
      } else {
        console.log('Harmonic order failed - success:', data.success, 'has tracks:', !!data.tracks);
      }
    } catch (err) {
      console.error('Error applying harmonic order:', err);
      alert('Failed to apply harmonic ordering');
    } finally {
      setIsApplyingAI(null);
    }
  };

  // Helper: Check if tracks have similar energy values
  const checkEnergySimilarity = (trackList) => {
    const energies = trackList.map(t => t.energy || t.energy_override || 5);
    const uniqueEnergies = [...new Set(energies)];
    const minEnergy = Math.min(...energies);
    const maxEnergy = Math.max(...energies);
    const range = maxEnergy - minEnergy;
    
    return {
      allSame: uniqueEnergies.length === 1,
      tooSimilar: range <= 2,
      range,
      uniqueCount: uniqueEnergies.length
    };
  };

  // Apply AI Energy Balancing (toggle on/off)
  const applyEnergyBalancing = async (curveType = 'journey') => {
    if (tracks.length < 3) return;
    
    // If already active, toggle OFF - restore original order
    if (aiOrderApplied === 'energy') {
      if (originalTrackOrder) {
        await reorderTracks(originalTrackOrder);
      }
      setAiOrderApplied(null);
      setOriginalTrackOrder(null);
      return;
    }
    
    // Check energy similarity before making the API call
    const energyCheck = checkEnergySimilarity(tracks);
    if (energyCheck.allSame) {
      alert('⚡ Energy Values Identical\n\nAll tracks have the same energy level. Energy Flow works best with varied energy values.\n\nTip: Run BEATSEEK Analysis to get accurate energy readings, or manually adjust energy levels by clicking the edit button on each track.');
      return;
    }
    
    // Store original order before applying AI (if not already stored)
    const currentOrder = [...tracks];
    if (!originalTrackOrder) {
      setOriginalTrackOrder(currentOrder);
    }
    
    setIsApplyingAI('energy');
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${BACKEND_URL}/api/crates/${crate.id}/ai/energy-balance?curve_type=${curveType}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to apply energy balancing');

      const data = await response.json();
      console.log('Energy balance response:', data.success, 'tracks:', data.tracks?.length);
      if (data.success && data.tracks) {
        console.log('Setting tracks from energy balance:', data.tracks.map(t => t.title).slice(0, 4));
        // Create new array with updated position values to force React re-render
        const reorderedTracks = data.tracks.map((t, i) => ({ ...t, position: i }));
        
        // Check if order actually changed
        const orderChanged = didOrderChange(currentOrder, reorderedTracks);
        
        setTracks(reorderedTracks);
        setTrackListKey(prev => prev + 1); // Force list re-render
        setAiOrderApplied('energy');
        
        // Alert user if no change detected
        if (!orderChanged && energyCheck.tooSimilar) {
          alert(`⚡ Energy Values Too Similar\n\nYour tracks have similar energy levels (range: ${energyCheck.range}).\n\nFor better results, add tracks with more varied energy or adjust values manually.`);
        } else if (!orderChanged) {
          alert('✨ Already Energy Optimized!\n\nYour tracks are already arranged in a good energy flow pattern.');
        }
      } else {
        console.log('Energy balance failed - success:', data.success, 'has tracks:', !!data.tracks);
      }
    } catch (err) {
      console.error('Error applying energy balance:', err);
      alert('Failed to apply energy balancing');
    } finally {
      setIsApplyingAI(null);
    }
  };

  // Revert to original track order with confirmation
  const revertToOriginalOrder = async () => {
    if (!originalTrackOrder) return;
    
    const confirmed = window.confirm(
      'You are reverting back to original track order. Proceed?'
    );
    
    if (confirmed) {
      await reorderTracks(originalTrackOrder);
      setAiOrderApplied(null);
      setOriginalTrackOrder(null);
    }
  };

  // BEATSEEK Audio Analysis - analyze tracks for BPM, Key, Energy, etc.
  const runBeatseekAnalysis = async () => {
    if (!analysisType) {
      alert('Please select an analysis type first.');
      return;
    }

    const totalTracks = tracks.length;
    setIsAnalyzing(true);
    setAnalysisProgress({ current: 0, total: totalTracks });

    // Simulate progress animation while backend processes
    let progressInterval;
    let simulatedProgress = 0;
    progressInterval = setInterval(() => {
      simulatedProgress += 1;
      if (simulatedProgress <= totalTracks - 1) {
        setAnalysisProgress({ current: simulatedProgress, total: totalTracks });
      }
    }, (totalTracks > 0 ? Math.max(500, 8000 / totalTracks) : 1000)); // Spread progress over ~8 seconds

    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/${crate.id}/analyze`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          analysis_type: analysisType
        })
      });

      clearInterval(progressInterval);
      setAnalysisProgress({ current: totalTracks, total: totalTracks }); // Complete

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Analysis failed');
      }

      const data = await response.json();
      if (data.tracks) {
        setTracks(data.tracks);
        fetchStats();
        const tierName = analysisType === 'core' ? 'Core Essentials' : 
                        analysisType === 'plus' ? 'Plus' : 
                        analysisType === 'full' ? 'Full Suite' : 'Advanced';
        
        // Show success message
        const successMsg = data.failed_count > 0 
          ? `🎧 Analyzed ${data.analyzed_count} tracks. ${data.failed_count} tracks couldn't be analyzed.`
          : `🎧 BEATSEEK Analysis Complete!\n\nAnalyzed ${data.analyzed_count || 0} tracks with ${tierName} analysis.`;
        alert(successMsg);
        
        // Show SoundCloud connection suggestion if it would help
        if (data.soundcloud_would_help && !data.soundcloud_connected) {
          setTimeout(() => {
            toast({
              title: "🎵 Tip: Connect SoundCloud",
              description: `${data.soundcloud_would_help_count} track(s) couldn't be analyzed. Connect your SoundCloud account for improved results, especially for underground and indie tracks.`,
              action: (
                <button 
                  onClick={() => {
                    window.location.href = `${BACKEND_URL}/api/soundcloud/auth/url`;
                  }}
                  style={{
                    background: '#ff5500',
                    color: 'white',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}
                >
                  Connect SoundCloud
                </button>
              ),
              duration: 15000
            });
          }, 500);
        }
      }
    } catch (err) {
      clearInterval(progressInterval);
      console.error('BEATSEEK Analysis error:', err);
      alert(`Analysis failed: ${err.message}`);
    } finally {
      setIsAnalyzing(false);
      setAnalysisProgress({ current: 0, total: 0 });
    }
  };

  // Clear BEATSEEK Analysis from all tracks
  const clearBeatseekAnalysis = async () => {
    if (!window.confirm('Clear BEATSEEK analysis display from tracks?\n\nThis removes the AI analysis panels. Core data (BPM, Key, Camelot, Energy) will be preserved so Harmonic Mix, Energy Flow, and Camelot Wheel continue to work.')) {
      return;
    }

    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/${crate.id}/clear-analysis`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to clear analysis');

      const data = await response.json();
      if (data.success && data.tracks) {
        setTracks(data.tracks);
        setAnalysisType(null);
        setTrackListKey(prev => prev + 1);
        alert('✓ Analysis panels cleared.\n\nCore mixing data (BPM, Key, Camelot, Energy) preserved for AI features.');
      }
    } catch (err) {
      console.error('Error clearing analysis:', err);
      alert('Failed to clear analysis data.');
    }
  };

  // Toggle collab mode
  const toggleCollabMode = async (mode) => {
    setIsUpdatingCollab(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/${crate.id}/collab-mode`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mode })
      });

      if (!response.ok) throw new Error('Failed to update collab mode');

      const data = await response.json();
      setCollabMode(data.collab_mode);
      setCrate(prev => ({ 
        ...prev, 
        collab_mode: data.collab_mode, 
        is_public: data.is_public,
        share_id: data.share_id
      }));
      if (onUpdate) onUpdate({ ...crate, collab_mode: data.collab_mode, is_public: data.is_public });
    } catch (err) {
      console.error('Error toggling collab mode:', err);
      alert('Failed to update collaboration mode');
    } finally {
      setIsUpdatingCollab(false);
    }
  };

  // Lock/unlock a track (host only)
  const handleLockTrack = async (position, shouldLock) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/${crate.id}/lock-tracks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ positions: [position], lock: shouldLock })
      });

      if (!response.ok) throw new Error('Failed to lock track');

      const data = await response.json();
      setLockedTracks(data.locked_tracks);
    } catch (err) {
      console.error('Error locking track:', err);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedTrack(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedTrack === null || draggedTrack === index) return;
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (draggedTrack !== null && dragOverIndex !== null && draggedTrack !== dragOverIndex) {
      const newTracks = [...tracks];
      const [removed] = newTracks.splice(draggedTrack, 1);
      newTracks.splice(dragOverIndex, 0, removed);
      reorderTracks(newTracks);
      // Clear AI ordering state when user manually reorders
      setAiOrderApplied(null);
      setOriginalTrackOrder(null);
    }
    setDraggedTrack(null);
    setDragOverIndex(null);
  };

  // Format duration
  const formatDuration = (ms) => {
    if (!ms) return '0:00';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="set-builder" data-testid="set-builder">
      {/* Header */}
      <div className="set-builder-header">
        <div className="header-left">
          <button className="back-btn" onClick={onClose} data-testid="back-to-crates">
            <ArrowLeft size={20} />
            Back
          </button>
          <div className="title-group">
            <h2>{crate.name}</h2>
            <span className={`crate-type-text ${crate.type}`}>
              {crate.type === 'live' ? 'Live Set' : 'Mock Set'}
            </span>
          </div>
        </div>

        <div className="header-actions">
          {/* AI-Powered Features Group */}
          <span className="ai-label">powered by AI ✨</span>
          <div className="ai-features-group">
            <button 
              className={`action-link ${showMixAdvisors ? 'active' : ''}`}
              onClick={() => setShowMixAdvisors(!showMixAdvisors)}
              disabled={tracks.length < 2}
              title="Toggle mix point advisors"
              data-testid="mix-advisor-toggle-btn"
            >
              <Lightbulb size={16} />
              Mix Advisor
            </button>
            <button 
              className={`action-link ${aiOrderApplied === 'harmonic' ? 'active' : ''}`}
              onClick={applyHarmonicOrdering}
              disabled={tracks.length < 2 || isApplyingAI}
              title="Auto-order tracks for smooth harmonic transitions"
              data-testid="harmonic-order-btn"
              >
                {isApplyingAI === 'harmonic' ? (
                  <Loader2 size={16} className="spinning" />
                ) : (
                  <Key size={16} />
                )}
                Harmonic Mix
              </button>
              <button 
                className={`action-link ${aiOrderApplied === 'energy' ? 'active' : ''}`}
                onClick={() => applyEnergyBalancing('journey')}
                disabled={tracks.length < 3 || isApplyingAI}
                title="Balance tracks for a dynamic energy journey"
                data-testid="energy-balance-btn"
              >
                {isApplyingAI === 'energy' ? (
                  <Loader2 size={16} className="spinning" />
                ) : (
                  <Waves size={16} />
                )}
                Energy Flow
              </button>
            <button 
              className="action-link"
              onClick={() => setShowCamelotWheel(true)}
              disabled={tracks.length === 0}
              title="Visualize harmonic journey on Camelot wheel"
              data-testid="camelot-wheel-btn"
            >
              <Disc size={16} />
              Camelot
            </button>
          </div>

          {/* Standard Actions */}
          <button 
            className="action-link"
            onClick={revertToOriginalOrder}
            disabled={!originalTrackOrder || isApplyingAI}
            title="Revert to original track order"
            data-testid="original-mix-btn"
          >
            <RefreshCw size={16} />
            Original Mix
          </button>
          <button 
            className="action-link"
            onClick={() => setShowSetPreview(true)}
            disabled={tracks.length === 0 || !tracks.some(t => t.preview_url)}
            title="Preview your set with crossfades"
            data-testid="preview-set-btn"
          >
            <Headphones size={16} />
            Preview
          </button>
          <button 
            className="action-link"
            onClick={() => setShowShareModal(true)}
            title="Share crate publicly or to Discord"
            data-testid="share-crate-btn"
          >
            <Share2 size={16} />
            Share
          </button>
          {/* Collaboration Chat Button */}
          {collabMode === 'collab_set' && (
            <button 
              className={`action-link ${showCollabPanel ? 'active' : ''}`}
              onClick={() => setShowCollabPanel(!showCollabPanel)}
              title="Open collaboration chat"
              data-testid="collab-chat-btn"
            >
              <MessageCircle size={16} />
              Chat
            </button>
          )}
          <button 
            className="action-link"
            onClick={() => setShowExportModal(true)}
            disabled={tracks.length === 0}
            data-testid="export-crate-btn"
          >
            <Download size={16} />
            Export
          </button>
          <button
            className={`action-link ${mbEnriching ? 'active' : ''}`}
            onClick={async () => {
              if (mbEnriching || tracks.length === 0) return;
              setMbEnriching(true);
              try {
                const updated = [...tracks];
                for (let i = 0; i < updated.length; i++) {
                  if (updated[i].mbTags) continue; // already enriched
                  const t = updated[i];
                  const params = new URLSearchParams({ title: t.title || '' });
                  if (t.artist) params.append('artist', t.artist);
                  const res = await fetch(`${BACKEND_URL}/api/musicbrainz/recording/search?${params}`);
                  const json = await res.json();
                  if (json.success && json.data?.tags?.length > 0) {
                    updated[i] = { ...updated[i], mbTags: json.data.tags.map(tg => tg.name) };
                  }
                }
                setTracks(updated);
                toast({ title: 'MusicBrainz Enrichment', description: `Tags enriched for ${tracks.length} tracks` });
              } catch (e) { console.error('MB enrich error:', e); }
              setMbEnriching(false);
            }}
            disabled={tracks.length === 0 || mbEnriching}
            title="Enrich tracks with MusicBrainz genre tags"
            data-testid="mb-enrich-btn"
          >
            {mbEnriching ? <Loader2 size={16} className="spinning" /> : <Sparkles size={16} />}
            MB Tags
          </button>
        </div>
      </div>

      {/* Collab Mode Selector with Stats */}
      <div className="collab-mode-bar">
        <div className="collab-left">
          <span className="collab-label">Collaboration & Privacy:</span>
          <div className="collab-mode-options">
            <button 
              className={`collab-link ${collabMode === 'private' ? 'active' : ''}`}
              onClick={() => toggleCollabMode('private')}
              disabled={isUpdatingCollab}
              data-testid="collab-mode-private"
            >
              <Lock size={14} />
              Private
            </button>
            <button 
              className={`collab-link ${collabMode === 'private_suggestions' ? 'active' : ''}`}
              onClick={() => toggleCollabMode('private_suggestions')}
              disabled={isUpdatingCollab}
              data-testid="collab-mode-suggestions"
            >
              <Users size={14} />
              + Suggestions
            </button>
            <button 
              className={`collab-link ${collabMode === 'public' ? 'active' : ''}`}
              onClick={() => toggleCollabMode('public')}
              disabled={isUpdatingCollab}
              data-testid="collab-mode-public"
            >
              <Share2 size={14} />
              Public
            </button>
            <button 
              className={`collab-link ${collabMode === 'collab_set' ? 'active' : ''}`}
              onClick={() => toggleCollabMode('collab_set')}
              disabled={isUpdatingCollab}
              data-testid="collab-mode-full"
            >
              <Users size={14} />
              Full Collab
            </button>
          </div>
          {isUpdatingCollab && <Loader2 size={16} className="spinning" />}
        </div>
        
        {/* Stats on right side of collab bar */}
        {stats && (
          <div className="collab-stats">
            <div className="mini-stat">
              <Music size={14} />
              <span>{stats.track_count} tracks</span>
            </div>
            <div className="mini-stat">
              <Clock size={14} />
              <span>{stats.total_duration_formatted}</span>
            </div>
            {stats.avg_energy && (
              <div className="mini-stat">
                <Zap size={14} />
                <span>Energy {stats.avg_energy}/10</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* BEATSEEK Audio Analysis Panel */}
      <div className="beatseek-analysis-panel">
        <div className="analysis-header">
          <span className="analysis-title">BEATSEEK Audio Analysis</span>
          <span className="powered-by">powered by AI</span>
        </div>
        
        <div className="analysis-options">
          <label className={`analysis-option ${analysisType === 'core' ? 'selected' : ''}`}>
            <input 
              type="radio" 
              name="analysisType" 
              checked={analysisType === 'core'}
              onChange={() => setAnalysisType('core')}
              disabled={isAnalyzing}
            />
            <span className="option-label">Core Essentials</span>
            <div 
              className="option-info"
              title="BPM • Key • Camelot Notation • Energy Level"
            >
              <Info size={14} />
              <div className="info-tooltip">
                <strong>Core Essentials:</strong>
                <ul>
                  <li>BPM Detection</li>
                  <li>Musical Key</li>
                  <li>Camelot Notation</li>
                  <li>Energy Level (1-10)</li>
                </ul>
              </div>
            </div>
          </label>
          
          <label className={`analysis-option ${analysisType === 'plus' ? 'selected' : ''}`}>
            <input 
              type="radio" 
              name="analysisType" 
              checked={analysisType === 'plus'}
              onChange={() => setAnalysisType('plus')}
              disabled={isAnalyzing}
            />
            <span className="option-label">Plus</span>
            <div 
              className="option-info"
              title="Everything in Core + Crate Building Features"
            >
              <Info size={14} />
              <div className="info-tooltip">
                <strong>Plus (Core +):</strong>
                <ul>
                  <li>All Core Essentials</li>
                  <li>Harmonic Compatibility Score</li>
                  <li>Energy Flow Curve</li>
                  <li>Transition Suggestions</li>
                </ul>
              </div>
            </div>
          </label>
          
          <label className={`analysis-option ${analysisType === 'full' ? 'selected' : ''}`}>
            <input 
              type="radio" 
              name="analysisType" 
              checked={analysisType === 'full'}
              onChange={() => setAnalysisType('full')}
              disabled={isAnalyzing}
            />
            <span className="option-label">Full Suite</span>
            <div 
              className="option-info"
              title="Everything in Plus + Advanced Analysis"
            >
              <Info size={14} />
              <div className="info-tooltip">
                <strong>Full Suite (Plus +):</strong>
                <ul>
                  <li>All Plus Features</li>
                  <li>Danceability Score</li>
                  <li>Mood/Vibe Detection</li>
                  <li>Vocal Presence %</li>
                  <li>Intro/Outro Length</li>
                  <li>Drop Detection</li>
                  <li>AI Mix-In/Out Points</li>
                </ul>
              </div>
            </div>
          </label>
          
          <label className={`analysis-option ${analysisType === 'advanced' ? 'selected' : ''}`}>
            <input 
              type="radio" 
              name="analysisType" 
              checked={analysisType === 'advanced'}
              onChange={() => setAnalysisType('advanced')}
              disabled={isAnalyzing}
            />
            <span className="option-label">Advanced</span>
            <div 
              className="option-info"
              title="Complete Analysis - Everything"
            >
              <Info size={14} />
              <div className="info-tooltip info-tooltip-wide">
                <strong>Advanced (Everything):</strong>
                <ul>
                  <li>All Full Suite Features</li>
                  <li>Genre/Subgenre Tags</li>
                  <li>Instrumentalness %</li>
                  <li>Acousticness Level</li>
                  <li>Peak Moment Timestamp</li>
                  <li>Energy Flow Curve</li>
                  <li>Spectral Analysis</li>
                  <li>Sound Palette Matching</li>
                </ul>
              </div>
            </div>
          </label>

          {/* Animated Gradient Progress Bar - Compact inline version */}
          {isAnalyzing && (
            <div className="analysis-progress-inline">
              <div className="progress-bar-mini">
                <div 
                  className="progress-fill-mini"
                  style={{ width: `${analysisProgress.total > 0 ? (analysisProgress.current / analysisProgress.total) * 100 : 0}%` }}
                />
              </div>
              <span className="progress-text-mini">{analysisProgress.current}/{analysisProgress.total}</span>
            </div>
          )}
        </div>
        
        <div className="analysis-buttons">
          <button 
            className={`analyze-btn ${analysisType ? 'active' : ''}`}
            onClick={runBeatseekAnalysis}
            disabled={!analysisType || isAnalyzing || tracks.length === 0}
          >
            {isAnalyzing ? (
              <>
                <Loader2 size={16} className="spinning" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles size={16} />
                Analyze
              </>
            )}
          </button>
          
          {/* Clear Analysis Button - only show if any tracks have analysis */}
          {tracks.some(t => t.beatseek_analyzed) && (
            <button 
              className="clear-analysis-btn"
              onClick={clearBeatseekAnalysis}
              disabled={isAnalyzing}
              title="Clear all analysis data from tracks"
            >
              <X size={14} />
              Clear Analysis
            </button>
          )}
        </div>
      </div>

      {/* Stats Bar - Now for BPM range, ownership, battle tested */}
      {stats && (
        <div className="stats-bar">
          {stats.bpm_range && (
            <div className="stat-item">
              <Zap size={16} />
              <span>{stats.bpm_range.min}-{stats.bpm_range.max} BPM</span>
            </div>
          )}
          
          {/* Helper text for missing BPM/Key */}
          <span className="bpm-key-helper">
            <em>Missing BPM/Key? Click <Edit3 size={12} /> on track to add manually</em>
          </span>
          
          {/* Right side items */}
          <div className="stats-right">
            {crate.battle_tested && (
              <div className="stat-item battle-tested">
                <Award size={16} />
                <span>Battle Tested</span>
              </div>
            )}
            <div className="stat-item ownership">
              <div className="ownership-text">
                <span>Ownership: {stats.ownership_percentage}%</span>
              </div>
              <div className="ownership-mini-bar">
                <div 
                  className="fill" 
                  style={{ width: `${stats.ownership_percentage}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Track List */}
      <div className="track-list-container">
        {tracks.length === 0 ? (
          <div className="empty-tracks">
            <Music size={48} strokeWidth={1} />
            <h3>No tracks yet</h3>
            <p>Add tracks to start building your set</p>
            <button 
              className="add-first-track-btn"
              onClick={() => setShowAddTrack(true)}
            >
              <Plus size={18} />
              Add Your First Track
            </button>
          </div>
        ) : (
          <div className="track-list" key={`track-list-${trackListKey}`}>
            {tracks.map((track, index) => {
              const prevTrack = index > 0 ? tracks[index - 1] : null;
              const transitionQuality = prevTrack 
                ? getTransitionQuality(
                    prevTrack.key_override || prevTrack.key,
                    track.key_override || track.key,
                    prevTrack.bpm_override || prevTrack.bpm,
                    track.bpm_override || track.bpm
                  )
                : null;

              return (
                <React.Fragment key={`${track.track_id}-${trackListKey}-${index}`}>
                  {/* Mix Point Advisor (between tracks) */}
                  {showMixAdvisors && index > 0 && (
                    <MixPointAdvisor
                      fromTrack={tracks[index - 1]}
                      toTrack={track}
                      isExpanded={expandedAdvisor === index}
                      onToggle={() => setExpandedAdvisor(expandedAdvisor === index ? null : index)}
                    />
                  )}

                  {/* Transition Indicator */}
                  {transitionQuality && !showMixAdvisors && (
                    <div className={`transition-indicator ${transitionQuality}`}>
                      <div className="transition-line" />
                      <span className="transition-label">
                        {transitionQuality === 'perfect' && '✓ Perfect'}
                        {transitionQuality === 'good' && '✓ Good'}
                        {transitionQuality === 'acceptable' && '~ OK'}
                        {transitionQuality === 'caution' && '⚠ Caution'}
                        {transitionQuality === 'unknown' && '?'}
                      </span>
                    </div>
                  )}

                  {/* Track Row */}
                  <div 
                    className={`track-row ${draggedTrack === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    data-testid={`track-row-${index}`}
                  >
                    {/* Drag Handle */}
                    <div className="drag-handle">
                      <GripVertical size={14} />
                    </div>

                    {/* Position */}
                    <div className="track-position">{index + 1}</div>

                    {/* Artwork */}
                    <div className="track-artwork">
                      {track.artwork_url ? (
                        <img src={track.artwork_url} alt="" />
                      ) : (
                        <div className="no-artwork">
                          <Music size={16} />
                        </div>
                      )}
                    </div>

                    {/* Main Info - Title & Artist */}
                    <div className="track-main">
                      <div className="track-title">{track.title}</div>
                      <div className="track-artist">{track.artist}</div>
                      {/* MusicBrainz tags inline */}
                      {track.mbTags && track.mbTags.length > 0 && (
                        <div data-testid={`mb-tags-${index}`} style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '3px' }}>
                          {track.mbTags.slice(0, 4).map((tag, ti) => (
                            <span key={ti} style={{
                              padding: '1px 5px', fontSize: '9px', fontWeight: 500,
                              background: 'rgba(255,0,255,0.08)', border: '1px solid rgba(255,0,255,0.15)',
                              borderRadius: '6px', color: '#ff66ff', lineHeight: '1.3',
                            }}>{tag}</span>
                          ))}
                        </div>
                      )}
                      {track.transition_notes && (
                        <div className="track-notes">
                          <Edit3 size={10} />
                          {track.transition_notes}
                        </div>
                      )}
                    </div>

                    {/* Metadata Column - Stacked */}
                    <div className="track-metadata">
                      <div className="meta-row">
                        <span className="meta-label"><Zap size={11} /></span>
                        <span className="meta-value">{track.bpm_override || track.bpm || '?'}</span>
                      </div>
                      <div className="meta-row">
                        <span className="meta-label"><Key size={11} /></span>
                        <span className="meta-value">{track.key_override || track.key || '?'}</span>
                      </div>
                      <div className="meta-row">
                        <span className="meta-label"><Clock size={11} /></span>
                        <span className="meta-value">{formatDuration(track.duration_ms)}</span>
                      </div>
                    </div>

                    {/* Badges Column - Stacked */}
                    <div className="track-badges">
                      <div className={`ownership-badge ${track.ownership_status}`}>
                        {track.ownership_status === 'verified' ? (
                          <><CheckCircle2 size={11} /> Owned</>
                        ) : (
                          <><XCircle size={11} /> Mock</>
                        )}
                      </div>
                      <div className={`audio-badge ${
                        track.spotify_uri ? 'full' : 
                        track.youtube_id ? 'full' : 
                        track.preview_url ? 'preview' : 'none'
                      }`}>
                        {track.spotify_uri ? (
                          <><Play size={10} /> Full</>
                        ) : track.youtube_id ? (
                          <><Play size={10} /> Full</>
                        ) : track.preview_url ? (
                          <><Play size={10} /> 30s</>
                        ) : (
                          <><AlertTriangle size={10} /> No Audio</>
                        )}
                      </div>
                    </div>

                    {/* Actions with Purchase Links */}
                    <div className="track-actions">
                      {/* Platform Purchase Links - always show all options */}
                      <div className="purchase-links">
                        {/* Beatport - Purchase */}
                        <a 
                          href={`https://www.beatport.com/search?q=${encodeURIComponent(track.artist + ' ' + track.title)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="platform-buy-link beatport"
                          onClick={(e) => e.stopPropagation()}
                          title="Buy on Beatport"
                        >
                          <img src="/logos/beatport-icon-clean.png" alt="Beatport" />
                        </a>
                        {/* Traxsource - Purchase */}
                        <a 
                          href={`https://www.traxsource.com/search?term=${encodeURIComponent(track.artist + ' ' + track.title)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="platform-buy-link traxsource"
                          onClick={(e) => e.stopPropagation()}
                          title="Buy on Traxsource"
                        >
                          <img src="/logos/traxsource-logo.png" alt="Traxsource" />
                        </a>
                        {/* Discogs - Purchase */}
                        <a 
                          href={track.source?.toLowerCase() === 'discogs' && track.external_url 
                            ? track.external_url 
                            : `https://www.discogs.com/search/?q=${encodeURIComponent(track.artist + ' ' + track.title)}&type=all`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="platform-buy-link discogs"
                          onClick={(e) => e.stopPropagation()}
                          title="Buy on Discogs"
                        >
                          <img src="/logos/discogs-logo-custom.svg" alt="Discogs" />
                        </a>
                        {/* Spotify - Stream */}
                        <a 
                          href={track.source?.toLowerCase() === 'spotify' && track.external_url 
                            ? track.external_url 
                            : `https://open.spotify.com/search/${encodeURIComponent(track.artist + ' ' + track.title)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="platform-buy-link spotify"
                          onClick={(e) => e.stopPropagation()}
                          title="Listen on Spotify"
                        >
                          <img src="/logos/spotify-logo.png" alt="Spotify" />
                        </a>
                        {/* Deezer - Stream */}
                        <a 
                          href={track.source?.toLowerCase() === 'deezer' && track.external_url 
                            ? track.external_url 
                            : `https://www.deezer.com/search/${encodeURIComponent(track.artist + ' ' + track.title)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="platform-buy-link deezer"
                          onClick={(e) => e.stopPropagation()}
                          title="Listen on Deezer"
                        >
                          <img src="/logos/deezer-logo.svg" alt="Deezer" />
                        </a>
                        {/* SoundCloud - Stream */}
                        <a 
                          href={track.source?.toLowerCase() === 'soundcloud' && track.external_url 
                            ? track.external_url 
                            : `https://soundcloud.com/search?q=${encodeURIComponent(track.artist + ' ' + track.title)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="platform-buy-link soundcloud"
                          onClick={(e) => e.stopPropagation()}
                          title="Listen on SoundCloud"
                        >
                          <img src="/logos/soundcloud-logo.svg" alt="SoundCloud" />
                        </a>
                      </div>
                      
                      {track.ownership_status !== 'verified' && (
                        <button 
                          className="swap-btn"
                          onClick={() => setSwappingTrack(index)}
                          title="Swap for verified track"
                          data-testid={`swap-track-btn-${index}`}
                        >
                          <RefreshCw size={14} />
                        </button>
                      )}
                      <button 
                        className="edit-btn"
                        onClick={() => setEditingTrack(index)}
                        title="Edit notes"
                      >
                        <Edit3 size={14} />
                      </button>
                      {track.external_url && (
                        <a 
                          href={track.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="external-btn"
                          title="Open source"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                      <button 
                        className="remove-btn"
                        onClick={() => removeTrack(index)}
                        title="Remove"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    {/* BEATSEEK AI Analysis - Compact Panel (Far Right) - Click to expand */}
                    {track.beatseek_analyzed && (
                      <div 
                        className="track-analysis-compact" 
                        data-testid={`track-analysis-${index}`}
                        onClick={() => setAnalysisDetailTrack(track)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setAnalysisDetailTrack(track)}
                      >
                        <div className="analysis-label">
                          <Sparkles size={10} />
                          <span>AI</span>
                        </div>
                        <div className="analysis-rows">
                          {/* Row 1: Core metrics - Always shown */}
                          <div className="analysis-row">
                            {(track.bpm || track.bpm_override) && (
                              <span className="ai-badge bpm" title="BPM">
                                <Zap size={10} />{track.bpm_override || track.bpm}
                              </span>
                            )}
                            {(track.key || track.key_override) && (
                              <span className="ai-badge key" title="Key">
                                <Key size={10} />{track.key_override || track.key}
                              </span>
                            )}
                            {track.camelot && (
                              <span className="ai-badge camelot" title="Camelot">
                                🎯{track.camelot}
                              </span>
                            )}
                            {track.energy && (
                              <span className="ai-badge energy" title="Energy Level (1-10)">
                                💥{track.energy}
                              </span>
                            )}
                          </div>
                          {/* Row 2: Plus metrics - Only if Plus/Full/Advanced tier */}
                          {(track.analysis_type === 'plus' || track.analysis_type === 'full' || track.analysis_type === 'advanced') && (
                            <div className="analysis-row">
                              {track.danceability !== undefined && (
                                <span className="ai-badge dance" title="Danceability">
                                  <Disc size={10} />{Math.round(track.danceability * 100)}%
                                </span>
                              )}
                              {track.vocal_presence !== undefined && (
                                <span className="ai-badge vocals" title={track.vocal_presence ? "Has Vocals" : "Instrumental"}>
                                  <Headphones size={10} />{track.vocal_presence ? 'Vox' : 'Inst'}
                                </span>
                              )}
                              {track.mood && (
                                <span className="ai-badge mood" title={`Mood: ${track.mood}`}>
                                  <Waves size={10} />{track.mood.split('/')[0].substring(0, 7)}
                                </span>
                              )}
                            </div>
                          )}
                          {/* Row 3: Full metrics - Only if Full/Advanced tier */}
                          {(track.analysis_type === 'full' || track.analysis_type === 'advanced') && (
                            <div className="analysis-row">
                              {track.mix_in_point && (
                                <span className="ai-badge mix-in" title="Suggested Mix-In Point">
                                  ▶{Math.round(track.mix_in_point)}s
                                </span>
                              )}
                              {track.mix_out_point && (
                                <span className="ai-badge mix-out" title="Suggested Mix-Out Point">
                                  ◀{Math.round(track.mix_out_point)}s
                                </span>
                              )}
                              {track.drops && track.drops.length > 0 && (
                                <span className="ai-badge drops" title={`${track.drops.length} Drop Points Detected`}>
                                  💥{track.drops.length}
                                </span>
                              )}
                              {track.intro_length !== undefined && (
                                <span className="ai-badge intro" title="Intro Length">
                                  ⏵{Math.round(track.intro_length)}s
                                </span>
                              )}
                              {track.outro_length !== undefined && (
                                <span className="ai-badge outro" title="Outro Length">
                                  ⏴{Math.round(track.outro_length)}s
                                </span>
                              )}
                            </div>
                          )}
                          {/* Row 4: Advanced metrics - Only if Advanced tier */}
                          {track.analysis_type === 'advanced' && (
                            <div className="analysis-row advanced">
                              {track.genre_tags && track.genre_tags.length > 0 && (
                                <span className="ai-badge genre" title={`Genres: ${track.genre_tags.join(', ')}`}>
                                  <Music size={10} />{track.genre_tags[0].substring(0, 8)}
                                </span>
                              )}
                              {track.instrumentalness !== undefined && (
                                <span className="ai-badge inst" title="Instrumentalness">
                                  🎹{Math.round(track.instrumentalness * 100)}%
                                </span>
                              )}
                              {track.acousticness !== undefined && (
                                <span className="ai-badge acoustic" title="Acousticness">
                                  🎸{Math.round(track.acousticness * 100)}%
                                </span>
                              )}
                              {track.peak_moment && (
                                <span className="ai-badge peak" title="Peak Energy Moment">
                                  🔥{Math.round(track.peak_moment)}s
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="analysis-expand-hint">
                          <span>click to expand</span>
                        </div>
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* Add Track Button */}
        {tracks.length > 0 && (
          <button 
            className="add-track-btn"
            onClick={() => setShowAddTrack(true)}
            data-testid="add-track-btn"
          >
            <Plus size={18} />
            Add Track
          </button>
        )}
      </div>

      {/* Add Track Modal */}
      {showAddTrack && (
        <AddTrackModal
          onClose={() => setShowAddTrack(false)}
          onAddFromDiscovery={() => {
            setShowAddTrack(false);
            if (onAddFromDiscovery) onAddFromDiscovery();
          }}
          onAddFromSpotify={() => {
            setShowAddTrack(false);
            if (onAddFromSpotify) onAddFromSpotify();
          }}
          onAddFromSoundCloud={() => {
            setShowAddTrack(false);
            if (onAddFromSoundCloud) onAddFromSoundCloud();
          }}
          onAddFromFavorites={() => {
            setShowAddTrack(false);
            if (onAddFromFavorites) onAddFromFavorites();
          }}
          onAddFromPlaylists={() => {
            setShowAddTrack(false);
            if (onAddFromPlaylists) onAddFromPlaylists();
          }}
          onAddFromLocal={() => {
            setShowAddTrack(false);
            if (onAddFromLocal) onAddFromLocal();
          }}
          spotifyConnected={spotifyConnected}
          soundcloudConnected={soundcloudConnected}
        />
      )}

      {/* Edit Track Modal */}
      {editingTrack !== null && (
        <EditTrackModal
          track={tracks[editingTrack]}
          position={editingTrack}
          onClose={() => setEditingTrack(null)}
          onSave={(updates) => updateTrack(editingTrack, updates)}
        />
      )}

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          crate={{
            ...crate,
            track_count: tracks.length,
            verified_count: tracks.filter(t => t.ownership_status === 'verified').length,
            mock_count: tracks.filter(t => t.ownership_status !== 'verified').length
          }}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          crate={crate}
          onClose={() => setShowShareModal(false)}
          onUpdate={(updates) => {
            setCrate(prev => ({ ...prev, ...updates }));
            if (onUpdate) onUpdate({ ...crate, ...updates });
          }}
        />
      )}

      {/* Swap Track Modal */}
      {swappingTrack !== null && tracks[swappingTrack] && (
        <SwapTrackModal
          track={tracks[swappingTrack]}
          position={swappingTrack}
          crateId={crate.id}
          onClose={() => setSwappingTrack(null)}
          onSwapped={(updatedTrack, newStats) => {
            setTracks(prev => prev.map((t, i) => 
              i === swappingTrack ? updatedTrack : t
            ));
            fetchStats();
          }}
        />
      )}

      {/* Camelot Wheel Modal */}
      {showCamelotWheel && (
        <div className="modal-overlay" onClick={() => setShowCamelotWheel(false)}>
          <div className="camelot-modal-wrapper">
            <div className="camelot-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Harmonic Journey</h3>
                <button className="close-btn" onClick={() => setShowCamelotWheel(false)}>×</button>
              </div>
              <div className="camelot-content">
                <CamelotWheel tracks={tracks} size={320} showJourney={true} />
                <div className="camelot-info">
                  <p>This wheel shows your set&apos;s harmonic journey. Keys that are adjacent on the wheel mix smoothly together.</p>
                  <div className="key-summary">
                    <span className="label">Keys used:</span>
                    <span className="value">
                      {[...new Set(tracks.map(t => t.key_override || t.key).filter(Boolean))].join(', ') || 'No keys set'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Set Preview Player */}
      {showSetPreview && (
        <SetPreviewPlayer
          tracks={tracks}
          onClose={() => setShowSetPreview(false)}
        />
      )}

      {/* Collab Discussion Panel */}
      <CollabDiscussionPanel
        isOpen={showCollabPanel}
        onClose={() => setShowCollabPanel(false)}
        crateId={crate.id}
        crateName={crate.name}
        isHost={true}
        tracks={tracks}
        lockedTracks={lockedTracks}
        onLockTrack={handleLockTrack}
      />

      {/* BEATSEEK AI Analysis Detail Modal */}
      {analysisDetailTrack && (
        <div 
          className="modal-overlay analysis-detail-overlay" 
          onClick={() => setAnalysisDetailTrack(null)}
          onKeyDown={(e) => e.key === 'Escape' && setAnalysisDetailTrack(null)}
        >
          <div className="analysis-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="analysis-detail-modal-inner">
              <div className="analysis-detail-header">
                <div className="header-title">
                  <Sparkles size={18} className="sparkle-icon" />
                  <span>BEATSEEK AI Analysis</span>
                </div>
                <button className="close-btn" onClick={() => setAnalysisDetailTrack(null)}>
                  <X size={18} />
                </button>
              </div>
              
              <div className="analysis-detail-track">
                <span className="track-name">{analysisDetailTrack.title}</span>
                <span className="track-artist">{analysisDetailTrack.artist}</span>
                <span className="analysis-tier">
                  Tier: {analysisDetailTrack.analysis_type === 'core' ? 'Core' : 
                         analysisDetailTrack.analysis_type === 'plus' ? 'Plus' : 
                         analysisDetailTrack.analysis_type === 'full' ? 'Full Suite' : 
                         analysisDetailTrack.analysis_type === 'advanced' ? 'Advanced' : 'Core'}
                </span>
              </div>

              <div className="analysis-detail-content">
              {/* CORE Section - Always shown */}
              <div className="analysis-section">
                <div className="section-header">
                  <span className="section-title">CORE</span>
                </div>
                <div className="section-grid">
                  <div className="detail-item">
                    <div className="item-icon"><Zap size={14} /></div>
                    <div className="item-content">
                      <span className="item-label">BPM</span>
                      <span className="item-value">{analysisDetailTrack.bpm_override || analysisDetailTrack.bpm || '—'}</span>
                      <span className="item-desc">Beats per minute</span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="item-icon"><Key size={14} /></div>
                    <div className="item-content">
                      <span className="item-label">Key</span>
                      <span className="item-value">{analysisDetailTrack.key_override || analysisDetailTrack.key || '—'}</span>
                      <span className="item-desc">Musical key detected</span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="item-icon">🎯</div>
                    <div className="item-content">
                      <span className="item-label">Camelot</span>
                      <span className="item-value">{analysisDetailTrack.camelot || '—'}</span>
                      <span className="item-desc">Harmonic mixing code</span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="item-icon">💥</div>
                    <div className="item-content">
                      <span className="item-label">Energy</span>
                      <span className="item-value">{analysisDetailTrack.energy || '—'}/10</span>
                      <span className="item-desc">Track intensity level</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* PLUS Section - Only if Plus/Full/Advanced */}
              {(analysisDetailTrack.analysis_type === 'plus' || analysisDetailTrack.analysis_type === 'full' || analysisDetailTrack.analysis_type === 'advanced') && (
                <div className="analysis-section">
                  <div className="section-header">
                    <span className="section-title">PLUS</span>
                  </div>
                  <div className="section-grid">
                    <div className="detail-item">
                      <div className="item-icon"><Disc size={14} /></div>
                      <div className="item-content">
                        <span className="item-label">Danceability</span>
                        <span className="item-value">{analysisDetailTrack.danceability !== undefined ? `${Math.round(analysisDetailTrack.danceability * 100)}%` : '—'}</span>
                        <span className="item-desc">How suitable for dancing</span>
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="item-icon"><Headphones size={14} /></div>
                      <div className="item-content">
                        <span className="item-label">Vocals</span>
                        <span className="item-value">{analysisDetailTrack.vocal_presence !== undefined ? (analysisDetailTrack.vocal_presence ? 'Present' : 'Instrumental') : '—'}</span>
                        <span className="item-desc">Vocal elements detected</span>
                      </div>
                    </div>
                    <div className="detail-item full-width">
                      <div className="item-icon"><Waves size={14} /></div>
                      <div className="item-content">
                        <span className="item-label">Mood</span>
                        <span className="item-value">{analysisDetailTrack.mood || '—'}</span>
                        <span className="item-desc">Overall emotional feel</span>
                      </div>
                    </div>
                    {analysisDetailTrack.harmonic_content && (
                      <div className="detail-item full-width">
                        <div className="item-icon"><Music size={14} /></div>
                        <div className="item-content">
                          <span className="item-label">Harmonic Content</span>
                          <span className="item-value">{analysisDetailTrack.harmonic_content}</span>
                          <span className="item-desc">Harmonic richness</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* FULL Section - Only if Full/Advanced */}
              {(analysisDetailTrack.analysis_type === 'full' || analysisDetailTrack.analysis_type === 'advanced') && (
                <div className="analysis-section">
                  <div className="section-header">
                    <span className="section-title">MIX POINTS</span>
                  </div>
                  <div className="section-grid">
                    <div className="detail-item">
                      <div className="item-icon mix-in">▶</div>
                      <div className="item-content">
                        <span className="item-label">Mix-In Point</span>
                        <span className="item-value">{analysisDetailTrack.mix_in_point ? `${Math.round(analysisDetailTrack.mix_in_point)}s` : '—'}</span>
                        <span className="item-desc">Start mixing here</span>
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="item-icon mix-out">◀</div>
                      <div className="item-content">
                        <span className="item-label">Mix-Out Point</span>
                        <span className="item-value">{analysisDetailTrack.mix_out_point ? `${Math.round(analysisDetailTrack.mix_out_point)}s` : '—'}</span>
                        <span className="item-desc">Exit mixing here</span>
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="item-icon drops">💥</div>
                      <div className="item-content">
                        <span className="item-label">Drops Detected</span>
                        <span className="item-value">{analysisDetailTrack.drops?.length || 0}</span>
                        <span className="item-desc">Energy peak moments</span>
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="item-icon">⏵</div>
                      <div className="item-content">
                        <span className="item-label">Intro Length</span>
                        <span className="item-value">{analysisDetailTrack.intro_length !== undefined ? `${Math.round(analysisDetailTrack.intro_length)}s` : '—'}</span>
                        <span className="item-desc">Before main elements</span>
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="item-icon">⏴</div>
                      <div className="item-content">
                        <span className="item-label">Outro Length</span>
                        <span className="item-value">{analysisDetailTrack.outro_length !== undefined ? `${Math.round(analysisDetailTrack.outro_length)}s` : '—'}</span>
                        <span className="item-desc">Fade-out duration</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ADVANCED Section - Only if Advanced */}
              {analysisDetailTrack.analysis_type === 'advanced' && (
                <div className="analysis-section">
                  <div className="section-header">
                    <span className="section-title">ADVANCED</span>
                  </div>
                  <div className="section-grid">
                    {analysisDetailTrack.genre_tags && analysisDetailTrack.genre_tags.length > 0 && (
                      <div className="detail-item full-width">
                        <div className="item-icon"><Music size={14} /></div>
                        <div className="item-content">
                          <span className="item-label">Genre</span>
                          <span className="item-value">{analysisDetailTrack.genre_tags.join(', ')}</span>
                          <span className="item-desc">AI-detected classification</span>
                        </div>
                      </div>
                    )}
                    <div className="detail-item">
                      <div className="item-icon">🎹</div>
                      <div className="item-content">
                        <span className="item-label">Instrumentalness</span>
                        <span className="item-value">{analysisDetailTrack.instrumentalness !== undefined ? `${Math.round(analysisDetailTrack.instrumentalness * 100)}%` : '—'}</span>
                        <span className="item-desc">Purely instrumental likelihood</span>
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="item-icon">🎸</div>
                      <div className="item-content">
                        <span className="item-label">Acousticness</span>
                        <span className="item-value">{analysisDetailTrack.acousticness !== undefined ? `${Math.round(analysisDetailTrack.acousticness * 100)}%` : '—'}</span>
                        <span className="item-desc">Acoustic vs electronic</span>
                      </div>
                    </div>
                    {analysisDetailTrack.peak_moment && (
                      <div className="detail-item">
                        <div className="item-icon">🔥</div>
                        <div className="item-content">
                          <span className="item-label">Peak Moment</span>
                          <span className="item-value">{Math.round(analysisDetailTrack.peak_moment)}s</span>
                          <span className="item-desc">Highest energy point</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Add Track Options Modal - Choose source for adding tracks
const AddTrackModal = ({ 
  onClose, 
  onAddFromDiscovery,
  onAddFromSpotify,
  onAddFromSoundCloud,
  onAddFromFavorites,
  onAddFromPlaylists,
  onAddFromLocal,
  spotifyConnected,
  soundcloudConnected
}) => {
  
  // Handle Spotify click - show alert if not connected
  const handleSpotifyClick = () => {
    if (!spotifyConnected) {
      const connect = window.confirm(
        '🎵 Spotify Premium Required\n\nTo browse your Spotify library, you need to connect your Spotify Premium account.\n\nWould you like to connect now?\n\nAlternatively, use Discovery or Local Upload to add tracks.'
      );
      if (connect) {
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/spotify/login?redirect=${encodeURIComponent(window.location.href)}`;
      }
      return;
    }
    onAddFromSpotify();
  };

  // Handle SoundCloud click - show alert if not connected
  const handleSoundCloudClick = () => {
    if (!soundcloudConnected) {
      const connect = window.confirm(
        '🔊 SoundCloud Account Required\n\nTo browse your SoundCloud library, you need to connect your SoundCloud account.\n\nWould you like to connect now?\n\nAlternatively, use Discovery or Local Upload to add tracks.'
      );
      if (connect) {
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/soundcloud/auth/url`;
      }
      return;
    }
    onAddFromSoundCloud();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="add-track-options-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add Track to Crate</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <p className="modal-subtitle">Choose where to find your track</p>

        <div className="add-track-options">
          {/* Discovery - Search all sources */}
          <button 
            className="add-track-option"
            onClick={onAddFromDiscovery}
            data-testid="add-from-discovery"
          >
            <div className="option-icon discovery">
              <Search size={28} />
            </div>
            <div className="option-info">
              <span className="option-title">Discovery</span>
              <span className="option-desc">Search Spotify, SoundCloud, Discogs & more</span>
            </div>
          </button>

          {/* Spotify Library */}
          <button 
            className="add-track-option"
            onClick={handleSpotifyClick}
            data-testid="add-from-spotify"
          >
            <div className="option-icon spotify">
              <img src="/logos/spotify-logo.png" alt="Spotify" style={{ width: 28, height: 28 }} />
            </div>
            <div className="option-info">
              <span className="option-title">Spotify Library</span>
              <span className="option-desc">
                {spotifyConnected ? 'Your playlists & saved tracks' : 'Connect Spotify Premium to browse'}
              </span>
            </div>
            {!spotifyConnected && <span className="option-badge">Connect</span>}
          </button>

          {/* SoundCloud */}
          <button 
            className="add-track-option"
            onClick={handleSoundCloudClick}
            data-testid="add-from-soundcloud"
          >
            <div className="option-icon soundcloud">
              <img src="/logos/soundcloud-logo.svg" alt="SoundCloud" style={{ width: 28, height: 28 }} />
            </div>
            <div className="option-info">
              <span className="option-title">SoundCloud</span>
              <span className="option-desc">
                {soundcloudConnected ? 'Your likes & playlists' : 'Connect SoundCloud to browse'}
              </span>
            </div>
            {!soundcloudConnected && <span className="option-badge">Connect</span>}
          </button>

          {/* Favorites */}
          <button 
            className="add-track-option"
            onClick={onAddFromFavorites}
            data-testid="add-from-favorites"
          >
            <div className="option-icon favorites">
              <span style={{ fontSize: 28 }}>❤️</span>
            </div>
            <div className="option-info">
              <span className="option-title">Favorites</span>
              <span className="option-desc">Tracks you&apos;ve saved in BEATSEEK</span>
            </div>
          </button>

          {/* BEATSEEK Playlists */}
          <button 
            className="add-track-option"
            onClick={onAddFromPlaylists}
            data-testid="add-from-playlists"
          >
            <div className="option-icon playlists">
              <span style={{ fontSize: 28 }}>📋</span>
            </div>
            <div className="option-info">
              <span className="option-title">BEATSEEK Playlists</span>
              <span className="option-desc">Add from your curated playlists</span>
            </div>
          </button>

          {/* Local Upload */}
          <button 
            className="add-track-option"
            onClick={onAddFromLocal}
            data-testid="add-from-local"
          >
            <div className="option-icon local">
              <span style={{ fontSize: 28 }}>📁</span>
            </div>
            <div className="option-info">
              <span className="option-title">Upload Track</span>
              <span className="option-desc">Add from your device (with metadata)</span>
            </div>
          </button>
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// Edit Track Modal
const EditTrackModal = ({ track, position, onClose, onSave }) => {
  const [notes, setNotes] = useState(track.transition_notes || '');
  const [bpmOverride, setBpmOverride] = useState(track.bpm_override || '');
  const [keyOverride, setKeyOverride] = useState(track.key_override || '');
  const [energyOverride, setEnergyOverride] = useState(track.energy_override || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({
        transition_notes: notes || null,
        bpm_override: bpmOverride ? parseFloat(bpmOverride) : null,
        key_override: keyOverride || null,
        energy_override: energyOverride ? parseInt(energyOverride) : null
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="edit-track-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit Track</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="track-preview">
          <div className="preview-artwork">
            {track.artwork_url ? (
              <img src={track.artwork_url} alt="" />
            ) : (
              <div className="no-artwork"><Music size={24} /></div>
            )}
          </div>
          <div className="preview-info">
            <div className="preview-title">{track.title}</div>
            <div className="preview-artist">{track.artist}</div>
          </div>
        </div>

        <div className="form-group">
          <label>Transition Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g., Mix on the breakdown, energy builds nicely"
            rows={3}
            data-testid="edit-track-notes"
          />
        </div>

        <div className="form-row triple">
          <div className="form-group">
            <label>BPM Override</label>
            <input
              type="number"
              value={bpmOverride}
              onChange={(e) => setBpmOverride(e.target.value)}
              placeholder={track.bpm || 'BPM'}
            />
          </div>
          <div className="form-group">
            <label>Key Override</label>
            <select
              value={keyOverride}
              onChange={(e) => setKeyOverride(e.target.value)}
            >
              <option value="">{track.key || 'Select key'}</option>
              {Object.keys(CAMELOT_WHEEL).map(key => (
                <option key={key} value={key}>{key}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Energy Override</label>
            <input
              type="number"
              value={energyOverride}
              onChange={(e) => setEnergyOverride(e.target.value)}
              placeholder={track.energy || '1-10'}
              min="1"
              max="10"
            />
          </div>
        </div>

        <div className="modal-actions">
          <button type="button" className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="save-btn"
            onClick={handleSave}
            disabled={isSaving}
            data-testid="save-track-edits"
          >
            {isSaving ? (
              <><Loader2 size={18} className="spinning" /> Saving...</>
            ) : (
              <><Save size={18} /> Save Changes</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetBuilder;
